import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, NgModule, OnInit } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { NavigationMode } from '@app/common/navigation-mode.';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { Navigation } from '@app/models/hubsite-data';
import { NavigationNode, SPNavigationNode } from '@app/models/navigation-node';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { LinkModule } from '@app/directives/link.directive';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { NavigationService } from '@app/services/navigation.service';
import { Environment } from '@environments/interface.environment';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-find-information-mega-menu',
  templateUrl: './find-information-mega-menu.component.html',
  styleUrls: ['./find-information-mega-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FindInformationMegaMenuComponent implements OnInit {

  navigationNodes$: Observable<SPNavigationNode[]>;
  enableCollapsableNodes = false;

  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private featureSettingsStoreService: FeatureSettingsStoreService,
    private service: NavigationService) { }

  ngOnInit() {

    this.enableCollapsableNodes = this.featureSettingsStoreService.state.enabledCollapsableNodesOnFindInformation ? true : false;

    if (this.featureSettingsStoreService.state.navigationMode === NavigationMode.HubSite) {
      this.navigationNodes$ = this.service
        .getUsersHomeHubSiteUrl()
        .pipe(
          mergeMap((siteUrl: string) => {
            return this.service.getHubSiteNavigation(siteUrl);
          }),
          map(n => {
            if (n?.navigation) {
              return this.mapHubSiteNodes(n.navigation);
            } else {
              return new Array<NavigationNode>();
            }
          }));
    } else {
      this.navigationNodes$ = this.service.getNavigationNodes()
        .pipe(map(n => {
          return this.mapSimplifiedTerms(n.filter(node => node.Terms));
        }));
    }
  }

  // Determines if node should show draw as level 2 (fat) or level 3 (thin) link
  // Menu has no level 3 nodes (or disabled) --> draw all as level 3
  // Node has children --> draw as levle 2
  // Node has no children --> draw as level 3 if no level 2 appeared above, else draw as level 2
  showAsSubHeader(node: NavigationNode, parent: NavigationNode) {

    // First check if level 3 nodes are completely disabled
    if (!this.featureSettingsStoreService.state.enableThirdLevelMenusInFindInformation) {
      return false;
    }

    // Is there a level 2 node above me?
    let thereAreNodesWithSubnodesAboveThis = false;
    let foundThis = false;
    for (let i = 0; i < parent.Children.length; i++) {
      if (parent.Children[i].Id === node.Id) {
        foundThis = true;
        break;
      }
      if (!foundThis) {
        if (parent.Children[i].Children && parent.Children[i].Children.length > 0) {
          thereAreNodesWithSubnodesAboveThis = true;
        }
      }
    }

    const nodeHasChildren = node.Children && node.Children.length > 0;

    if (!nodeHasChildren && thereAreNodesWithSubnodesAboveThis) {
      return true;
    }

    return nodeHasChildren;
  }

  getHubSiteUrl(url: string): string {
    if (url && url.toLowerCase().indexOf('linkless.header') === -1) {
      if (url.length > 0) {
        return url[0] === '/' ? this.environment.tenantHostUrl + url : url;
      }
      return url;
    } else {
      return null;
    }
  }

  mapHubSiteNodes(nodes: Navigation[]) {
    return nodes.map(node => {
      return new SPNavigationNode({
        Title: node.Title,
        Url: this.getHubSiteUrl(node.Url),
        Id: node.Id.toString(),
        IsDocLib: node.IsDocLib,
        IsExternal: node.IsExternal,
        ParentId: node.ParentId,
        ListTemplateType: node.ListTemplateType,
        AudienceIds: node.AudienceIds,
        IsAvailableForTagging: true,
        Children: this.mapHubSiteNodes(node.Children)
      });
    });
  }

  mapSimplifiedTerms(nodes: SimplifiedTerm[]) {
    return nodes.map(node => {
      return new SPNavigationNode({
        Title: node.Name,
        Url: node.Url,
        Id: node.Guid,
        FriendlyUrl: node.FriendlyUrl,
        TermCount: node.TermCount,
        LastModified: node.LastModified,
        Labels: node.Labels,
        LocalCustomProperties: node.LocalCustomProperties,
        Selected: node.Selected,
        DisableSelection: node.DisableSelection,
        IsRoot: node.IsRoot,
        IsAvailableForTagging: node.IsAvailableForTagging,
        Children: node.Terms ? this.mapSimplifiedTerms(node.Terms) : []
      });
    });
  }

}
@NgModule({
  declarations: [FindInformationMegaMenuComponent],
  exports: [
    FindInformationMegaMenuComponent
  ],
  imports: [
    CommonModule,
    WidgetPlaceholderModule,
    LinkModule,
  ]
})
export class FindInformationMegaMenuModule { }
